import { cast } from '@/hexapla-core/generics/cast'
import { Lemma } from '@/hexapla-core/domains/lemmas'
import { identifiesLemma, locatesLemma } from '@/hexapla-core/domains/lemmas/span'

export interface UseNewLemmaGuards {
  navigation: Navigation[]
}

interface EnterGuard {
  (newLemma: Lemma): boolean
}

export interface Navigation {
  name: string
  text: string
  index: number
  canEnter: EnterGuard
  nextError?: string
}

/**
 * The wizard's steps: their labels, their order, and what each one needs before
 * it can be entered.
 *
 * The single source of truth — the router (`router/routes/lemmas`) builds its
 * children from this list and the footer (`Navigator.vue`) renders from it. All
 * three used to keep their own copy and had already drifted: this file declared
 * a `lemmas/new/comments` step that neither of the others had, and the two
 * "cannot continue" messages differed.
 */
export const navigation: Navigation[] = [
  {
    name: 'lemmas/new/text',
    text: 'Texts',
    index: 0,
    canEnter: ({}: Lemma): boolean => true,
  },
  {
    name: 'lemmas/new/lemma',
    text: 'Lemma',
    index: 1,
    canEnter: (newLemma: Lemma): boolean => !!newLemma!.texts.length,
    nextError: 'Must have at least 1 text',
  },
  {
    name: 'lemmas/new/attestations',
    text: 'Hexaplaric Readings',
    index: 2,
    // The API's rule for a lemma that may be completed: a selection, an
    // insertion point, its Hebrew, or context on either line (Job 39:12a).
    canEnter: (newLemma: Lemma): boolean =>
      !!newLemma.texts.length && identifiesLemma(newLemma) && locatesLemma(newLemma),
    nextError: 'Select the lemma, give its Hebrew, or give context',
  },
  {
    name: 'lemmas/new/review',
    text: 'Review',
    index: 3,
    canEnter: ({}: Lemma): boolean => true,
    nextError: 'Must have at least 1 hexaplaric reading',
  },
]

export const useNewLemmaGuards = (): UseNewLemmaGuards => {
  return { navigation }
}

export default cast<UseNewLemmaGuards>({ navigation })
